import {
  Dialog,
  TextField,
  Button,
  Slide,
  Typography,
  Box,
  useTheme,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { KeyRound, X, Eye, EyeOff, Lock, ShieldCheck, CheckCircle } from "lucide-react";
import { useState, useEffect, forwardRef } from "react";

const SlideTransition = forwardRef(function SlideTransition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function ResetPasswordModal({ open, onClose, onSubmit }) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const theme = useTheme();

  const passwordsMatch = password && confirmPassword && password === confirmPassword;

  useEffect(() => {
    if (!open) {
      setPassword("");
      setConfirmPassword("");
      setShowPassword(false);
      setShowConfirm(false);
    }
  }, [open]);

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: "#F9FAFB",
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  };

  const labelSx = {
    fontWeight: 700,
    fontSize: 11,
    mb: 0.75,
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={SlideTransition}
      TransitionProps={{ timeout: 300 }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: "0 25px 60px -12px rgba(0, 0, 0, 0.2)",
          width: 440,
          maxWidth: "95vw",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          px: 4,
          pt: 4,
          pb: 3,
          "@keyframes fadeInUp": {
            from: { opacity: 0, transform: "translateY(10px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            bgcolor: `${theme.palette.primary.main}12`,
            border: `2px solid ${theme.palette.primary.main}25`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2.5,
            animation: "fadeInUp 0.35s ease forwards",
          }}
        >
          <ShieldCheck
            size={24}
            color={theme.palette.primary.main}
            strokeWidth={1.8}
          />
        </Box>

        {/* Title */}
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: 20,
            color: "#111827",
            mb: 0.5,
            animation: "fadeInUp 0.35s 0.05s ease both",
          }}
        >
          Reset Password
        </Typography>

        {/* Subtitle */}
        <Typography
          sx={{
            fontSize: 13,
            color: "#9CA3AF",
            textAlign: "center",
            mb: 3,
            animation: "fadeInUp 0.35s 0.1s ease both",
          }}
        >
          Please enter a new secure password for this user.
        </Typography>

        {/* Password Fields */}
        <Box
          sx={{
            width: "100%",
            animation: "fadeInUp 0.35s 0.15s ease both",
          }}
        >
          <Typography sx={labelSx}>New Password</Typography>
          <TextField
            fullWidth
            size="small"
            type={showPassword ? "text" : "password"}
            placeholder="Enter new password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock size={16} color="#9CA3AF" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                    sx={{ color: "#9CA3AF" }}
                  >
                    {showPassword ? (
                      <EyeOff size={16} />
                    ) : (
                      <Eye size={16} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ ...inputSx, mb: 2.5 }}
          />

          <Typography sx={labelSx}>Confirm New Password</Typography>
          <TextField
            fullWidth
            size="small"
            type={showConfirm ? "text" : "password"}
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={
              confirmPassword.length > 0 &&
              password !== confirmPassword
            }
            helperText={
              confirmPassword.length > 0 &&
              password !== confirmPassword
                ? "Passwords do not match"
                : ""
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock size={16} color="#9CA3AF" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirm(!showConfirm)}
                    edge="end"
                    size="small"
                    sx={{ color: "#9CA3AF" }}
                  >
                    {showConfirm ? (
                      <EyeOff size={16} />
                    ) : (
                      <Eye size={16} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={inputSx}
          />
        </Box>

        {/* Actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            width: "100%",
            gap: 1.5,
            mt: 3.5,
            animation: "fadeInUp 0.35s 0.2s ease both",
          }}
        >
          <Button
            onClick={onClose}
            sx={{
              textTransform: "uppercase",
              borderRadius: 2,
              px: 3,
              py: 1,
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.04em",
              color: theme.palette.primary.main,
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: `${theme.palette.primary.main}08`,
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            startIcon={<CheckCircle size={16} />}
            onClick={() => passwordsMatch && onSubmit(password)}
            disabled={!passwordsMatch}
            sx={{
              textTransform: "uppercase",
              borderRadius: 3,
              px: 3.5,
              py: 1,
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.04em",
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.avatarGradients_1?.avatarEnd || theme.palette.primary.main} 100%)`,
              color: "#fff",
              boxShadow: `0 4px 14px ${theme.palette.primary.main}30`,
              transition:
                "all 0.25s cubic-bezier(0.23, 1, 0.32, 1)",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: `0 6px 20px ${theme.palette.primary.main}40`,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.avatarGradients_1?.avatarEnd || theme.palette.primary.main} 100%)`,
              },
              "&:active": {
                transform: "translateY(0)",
              },
              "&.Mui-disabled": {
                background: "#E5E7EB",
                color: "#9CA3AF",
                boxShadow: "none",
              },
            }}
          >
            Update
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
